import React, { useEffect, useState } from 'react';
import MainTopBar from '../../ui/panels/TopBars/MainTopBar/MainTopBar';
import { LoginState } from '../../types/LoginState';
import { UserUtils } from '../../utils/UserUtils';

const rootStyle: React.CSSProperties = {
  minHeight: '100vh',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  background:
    'radial-gradient(circle at top left, rgba(252,101,52,0.09), rgba(255,247,237,1)), radial-gradient(circle at bottom right, rgba(245,190,100,0.35), rgba(255,255,255,1))',
  color: '#111827',
};

const pageStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const cardStyle: React.CSSProperties = {
  maxWidth: '760px',
  padding: '3.5rem 3rem',
  borderRadius: '1.75rem',
  background:
    'linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,250,240,0.98)) border-box',
  boxShadow:
    '0 26px 60px rgba(148, 163, 184, 0.35), 0 0 0 1px rgba(248, 250, 252, 0.9)',
  position: 'relative',
  overflow: 'hidden',
};

const softShapeLeft: React.CSSProperties = {
  position: 'absolute',
  width: '11rem',
  height: '11rem',
  borderRadius: '999px',
  left: '-4rem',
  top: '-4rem',
  background:
    'radial-gradient(circle, rgba(252,101,52,0.12) 0%, rgba(252,101,52,0) 70%)',
};

const softShapeRight: React.CSSProperties = {
  position: 'absolute',
  width: '14rem',
  height: '14rem',
  borderRadius: '999px',
  right: '-6rem',
  bottom: '-6rem',
  background:
    'radial-gradient(circle, rgba(245,190,100,0.28) 0%, rgba(245,190,100,0) 70%)',
};

const contentInnerStyle: React.CSSProperties = {
  position: 'relative',
  zIndex: 1,
};

const badgeRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: '0.75rem',
  marginBottom: '1.75rem',
  flexWrap: 'wrap',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.45rem',
  padding: '0.35rem 0.9rem',
  borderRadius: '999px',
  fontSize: '0.75rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  backgroundColor: 'rgba(255,255,255,0.85)',
  color: '#b45309',
  border: '1px solid rgba(245, 190, 100, 0.9)',
  boxShadow: '0 8px 18px rgba(248, 181, 80, 0.35)',
};

const dotStyle: React.CSSProperties = {
  width: '0.45rem',
  height: '0.45rem',
  borderRadius: '999px',
  background:
    'radial-gradient(circle at 30% 30%, rgba(34,197,94,1), rgba(22,163,74,1))',
  boxShadow: '0 0 0 5px rgba(110, 231, 183, 0.6)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(2.4rem, 5vw, 3.3rem)',
  lineHeight: 1.25,
  fontWeight: 800,
  letterSpacing: '-0.04em',
  margin: 0,
  marginBottom: '1.25rem',
};

const highlightStyle: React.CSSProperties = {
  background:
    'linear-gradient(120deg, rgba(250,204,21,0.35), rgba(245,158,11,0.1))',
  boxShadow: '0 0 0 0 rgba(250,204,21,0.35)',
  borderRadius: '999px',
  padding: '0.05rem 0.65rem',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: '#4b5563',
  maxWidth: '34rem',
  margin: '0 auto 2.25rem auto',
};

const pillRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '0.7rem',
  fontSize: '0.8rem',
  color: '#374151',
};

const pillStyle: React.CSSProperties = {
  padding: '0.4rem 0.95rem',
  borderRadius: '999px',
  backgroundColor: 'rgba(255,255,255,0.9)',
  border: '1px solid rgba(209, 213, 219, 0.9)',
  boxShadow: '0 10px 25px rgba(148, 163, 184, 0.35)',
};

const statusBarStyle: React.CSSProperties = {
  marginTop: '2.5rem',
  padding: '0.9rem 1.1rem',
  borderRadius: '0.9rem',
  backgroundColor: 'rgba(255,255,255,0.92)',
  border: '1px solid rgba(248, 181, 80, 0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1.25rem',
  boxShadow: '0 14px 30px rgba(248, 181, 80, 0.35)',
};

const statusItemStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'left',
};

const statusLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  color: '#6b7280',
  letterSpacing: '0.08em',
  marginBottom: '0.2rem',
};

const statusValueStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#111827',
};

const dividerStyle: React.CSSProperties = {
  width: '1px',
  alignSelf: 'stretch',
  background:
    'linear-gradient(to bottom, rgba(209,213,219,0), rgba(209,213,219,0.9), rgba(209,213,219,0))',
};

const barTrackStyle: React.CSSProperties = {
  marginTop: '0.45rem',
  width: '100%',
  height: '0.3rem',
  borderRadius: '999px',
  backgroundColor: '#f3f4f6',
  overflow: 'hidden',
};

const barFillStyle: React.CSSProperties = {
  width: '72%',
  height: '100%',
  borderRadius: '999px',
  background:
    'linear-gradient(90deg, rgba(250,204,21,1), rgba(245,158,11,1))',
};

const Home: React.FC = () => {
  const [loginState, setLoginState] = useState<LoginState>({
    isLoggedIn: false,
    isAdmin: false,
  });

  const token = localStorage.getItem('token');

  // Get current user name from token
  const currentUserName = UserUtils.getCurrentUserName();

  const validateUserLogin = async () => {
    if (token) {
      const url = '/validate';
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setLoginState({ isLoggedIn: false, isAdmin: false });
        return;
      }

      const json = await response.json();

      if (json.valid && json.valid === true) {
        setLoginState({ isLoggedIn: true, isAdmin: true });
      } else {
        setLoginState({ isLoggedIn: true, isAdmin: false });
      }
    }
  };

  useEffect(() => {
    validateUserLogin();
  }, []);

  return (
    <div style={rootStyle}>
      <MainTopBar
        isLoggedIn={loginState.isLoggedIn}
        isSticky={true}
        showAdminButton={loginState.isAdmin}
        userName={currentUserName}
      />
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={softShapeLeft} />
          <div style={softShapeRight} />
          <div style={contentInnerStyle}>
            <div style={badgeRowStyle}>
              <div style={badgeStyle}>
                <span style={dotStyle} />
                <span>MedBuddy AI for Healthcare Teams</span>
              </div>
            </div>
            <h1 style={titleStyle}>
              <span>Manage Your Patient Records with </span>
              <span style={highlightStyle}>MedBuddy AI</span>
            </h1>
            <p style={subtitleStyle}>
              A clear, calming workspace for every consultation, report, and
              follow-up so you can focus on your patients, not the paperwork.
            </p>
            <div style={statusBarStyle}>
              <div style={statusItemStyle}>
                <div style={statusLabelStyle}>Designed for</div>
                <div style={statusValueStyle}>Clinicians & care teams</div>
              </div>
              <div style={dividerStyle} />
              <div style={statusItemStyle}>
                <div style={statusLabelStyle}>What you get</div>
                <div style={statusValueStyle}>AI-ready patient records</div>
              </div>
              <div style={dividerStyle} />
              <div style={statusItemStyle}>
                <div style={statusLabelStyle}>Focus time</div>
                <div style={statusValueStyle}>More time with every patient</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
